import { getReportFooterText } from '../reportFooter';
export interface InventoryTrendsHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
}

export interface InventoryTrendsItem {
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  totalQtyIssued?: number | null;
  avgMonthlyIssued?: number | null;
  prev30DaysIssued?: number | null;
  trendPercent?: number | null;
}

export interface InventoryTrendsTemplateOptions {
  generatedAt: Date;
  header: InventoryTrendsHeaderInfo;
  items: InventoryTrendsItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatPercent = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  const pct = value;
  const formatted = `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`;
  return formatted;
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: InventoryTrendsItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="7" class="empty-row">
          No inventory trend data was found for the selected period.
        </td>
      </tr>
    `;
  }

  return items
    .map((item) => {
      const trend = item.trendPercent ?? null;
      const trendLabel = formatPercent(trend);
      let trendClass = 'trend-neutral';
      if (typeof trend === 'number' && Number.isFinite(trend)) {
        if (trend > 0) trendClass = 'trend-positive';
        else if (trend < 0) trendClass = 'trend-negative';
      }
      return `
        <tr>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric center">${formatNumber(item.totalQtyIssued ?? null)}</td>
          <td class="numeric center">${formatNumber(item.avgMonthlyIssued ?? null)}</td>
          <td class="numeric center">${formatNumber(item.prev30DaysIssued ?? null)}</td>
          <td class="numeric center ${trendClass}">${trendLabel}</td>
        </tr>
      `;
    })
    .join('');
};

export const buildInventoryTrendsReportHtml = ({
  generatedAt,
  header,
  items,
}: InventoryTrendsTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const rangeLabel =
    header.fromDate || header.toDate
      ? `${formatDate(header.fromDate ?? null)} – ${formatDate(header.toDate ?? null)}`
      : '—';
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Inventory Trends Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
      }

      .title-block {
        flex: 1;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
      }

      .range-label {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .generated {
        font-size: 12px;
        color: #5c6f85;
        text-align: right;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .center {
        text-align: center;
      }

      .trend-positive {
        color: #0a8a0a;
        font-weight: 600;
      }

      .trend-negative {
        color: #c62828;
        font-weight: 600;
      }

      .trend-neutral {
        color: #4a5c72;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="title-block">
        <h1 class="title">Inventory Trends Report</h1>
        <div class="range-label">${sanitizeText(rangeLabel)}</div>
      </div>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th class="center">Total Qty Issued</th>
            <th class="center">Avg Monthly Issued</th>
            <th class="center">Prev 30 Days Issued</th>
            <th class="center">Trend</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
